import { type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import {
  IconApps,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconServer,
  IconBox,
} from "@tabler/icons-react";
import { useAuth } from "../../auth/AuthContext.js";
import { Logo } from "../../components/Logo.js";
import { ThemeToggle } from "../../components/ThemeToggle.js";
import { UserMenu } from "../../components/UserMenu.js";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Match route exactly (vs. prefix). Use for index-style routes. */
  end?: boolean;
  /** Reserved for future feature work — items with `disabled` render but
   *  are not clickable. Lets the layout show roadmap surface without
   *  shipping routes. */
  disabled?: boolean;
}

interface NavSection {
  /** Optional section header. Omit for an unlabelled top section. */
  label?: string;
  items: NavItem[];
}

/**
 * NAV config — single source of truth for the sidebar.
 *
 * Today only "Virtual Machines" is shipped. Future feature surfaces are
 * declared but `disabled` so the layout already accommodates them —
 * wiring them up later means flipping `disabled` to false and adding
 * the matching `<Route>` in App.tsx, nothing else.
 */
const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        to: "/dashboard",
        label: "Virtual Machines",
        icon: <IconServer size={18} stroke={1.75} />,
        end: true,
      },
      {
        to: "/dashboard/containers",
        label: "Containers",
        icon: <IconBox size={18} stroke={1.75} />,
        // No `end: true` here: `/dashboard/containers` is not a prefix of
        // `/dashboard`, so prefix-match is safe and lets `activeLabel()`
        // + NavLink active-highlighting fire on BOTH the index route and
        // detail routes (`/dashboard/containers/:vmid`). The VM item above
        // MUST keep `end:true` because `/dashboard` is a prefix of every
        // customer route — removing it there would highlight VMs everywhere.
      },
      {
        to: "/dashboard/apps",
        label: "Apps",
        icon: <IconApps size={18} stroke={1.75} />,
        // No `end: true` — same rule as Containers above: the catalog
        // (`/dashboard/apps/catalog/:slug`) and instance
        // (`/dashboard/apps/instances/:id`) detail routes nest under this
        // prefix and should keep the item highlighted.
      },
    ],
  },
  // ── Future — flip `disabled: false` and add a route when shipping ──────
  // {
  //   label: "Storage",
  //   items: [
  //     { to: "/dashboard/snapshots", label: "Snapshots", icon: <IconHistory size={18} stroke={1.75} />, disabled: true },
  //     { to: "/dashboard/backups",   label: "Backups",   icon: <IconArchive size={18} stroke={1.75} />, disabled: true },
  //   ],
  // },
  // {
  //   label: "Networking",
  //   items: [
  //     { to: "/dashboard/networking/vlans",  label: "VLANs",  icon: <IconLan size={18} stroke={1.75} />, disabled: true },
  //     { to: "/dashboard/networking/vxlans", label: "VXLANs", icon: <IconNetwork size={18} stroke={1.75} />, disabled: true },
  //   ],
  // },
  // {
  //   label: "Deploy",
  //   items: [
  //     { to: "/dashboard/images", label: "Images", icon: <IconPhoto size={18} stroke={1.75} />, disabled: true },
  //   ],
  // },
];

/**
 * Customer app shell.
 *
 * Layout summary:
 * - Desktop (≥ sm): persistent left navbar. User can toggle collapse to
 *   an icon-only rail (state persisted in localStorage so it survives
 *   reload). Width: 248px expanded / 76px collapsed.
 * - Mobile (< sm): navbar hidden; opens as overlay drawer via the burger
 *   in the header. The drawer always shows the full label + icon for
 *   every nav item — it is independent of the desktop collapse
 *   preference (see `(!collapsed || mobileOpen)` gating in
 *   `renderNavItem`).
 * - Header (56px): left has burger on mobile / collapse toggle on desktop;
 *   center-left shows the active page name; right has theme toggle +
 *   user menu.
 * - Body: `<Outlet/>` from react-router renders the active page (Dashboard,
 *   VmDetail, future snapshots/backups/etc.) inside `AppShell.Main`.
 */
export function CustomerLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();
  const [collapsed, setCollapsed] = useLocalStorage<boolean>({
    key: "proxvps-customer-nav-collapsed",
    defaultValue: false,
  });
  const [mobileOpen, { toggle: toggleMobile }] = useDisclosure();

  function activeLabel(): string {
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        if (item.disabled) continue;
        const matchExact = item.end && location.pathname === item.to;
        const matchPrefix = !item.end && location.pathname.startsWith(item.to);
        if (matchExact || matchPrefix) return item.label;
      }
    }
    return "Virtual Machines";
  }

  async function onLogout() {
    await auth.logout();
    navigate("/login", { replace: true });
  }

  function renderNavItem(item: NavItem) {
    const link = (
      <NavLink to={item.to} end={item.end} style={{ textDecoration: "none" }}>
        {({ isActive }) => (
          <Box
            data-active={isActive || undefined}
            className="proxvps-nav-item"
            px="sm"
            py={8}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              borderRadius: theme.radius.md,
              // CRITICAL: an explicit color is required on the nav Box
              // because the underlying <a> (NavLink) inherits the browser
              // default link color (blue) for unstyled anchors — without
              // this override, inactive items render as raw hyperlink
              // blue in both modes, and the active item uses the wrong
              // text/bg pairing. --mantine-color-text adapts to the
              // active color scheme; --mantine-primary-color-light-color
              // is the token designed to sit on top of -light bg (the
              // saturated brand shade in light mode, the brand-5 shade
              // in dark mode). Hover affordance lives in the
              // .proxvps-nav-item class in index.html.
              color: isActive
                ? "var(--mantine-primary-color-light-color)"
                : "var(--mantine-color-text)",
              // Inactive must be `undefined`, NOT "transparent" — an inline
              // background-color carries specificity (1,0,0,0) and would
              // shadow the .proxvps-nav-item:hover class rule (specificity
              // 0,3,0). Leaving it undefined lets the class win on hover,
              // matching the .proxvps-hover-chip convention in UserMenu.tsx.
              // Box default background is already transparent so the base
              // visual is identical.
              backgroundColor: isActive
                ? "var(--mantine-primary-color-light)"
                : undefined,
              fontWeight: isActive ? 600 : 500,
              fontSize: theme.fontSizes.sm,
              cursor: item.disabled ? "not-allowed" : "pointer",
              opacity: item.disabled ? 0.45 : 1,
            }}
          >
            <Box style={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
              {item.icon}
            </Box>
            {/*
              Label visibility rules:
              - Mobile drawer (mobileOpen=true): ALWAYS show — the drawer
                is independent of the desktop collapse preference. The
                component docstring promises this.
              - Desktop expanded (!collapsed): show.
              - Desktop collapsed (collapsed && !mobileOpen): hide — the
                Tooltip wrapper below provides the affordance instead.
            */}
            {(!collapsed || mobileOpen) && (
              <Text size="sm" fw={isActive ? 600 : 500} style={{ whiteSpace: "nowrap" }}>
                {item.label}
              </Text>
            )}
          </Box>
        )}
      </NavLink>
    );

    // Only wrap in a tooltip when in desktop-collapsed (icon rail) mode —
    // in the mobile drawer the label is already visible and tooltips on
    // touch are awkward / unreliable.
    if (collapsed && !mobileOpen) {
      return (
        <Tooltip key={item.to} label={item.label} position="right" withArrow>
          {link}
        </Tooltip>
      );
    }
    return <Box key={item.to}>{link}</Box>;
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: { base: 264, sm: collapsed ? 76 : 248 },
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpen },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" gap="md">
          <Group gap="sm" wrap="nowrap">
            {/* Mobile: drawer toggle. Desktop: hidden. */}
            <Burger
              opened={mobileOpen}
              onClick={toggleMobile}
              size="sm"
              hiddenFrom="sm"
              aria-label="Toggle navigation"
            />
            {/* Desktop: collapse-to-rail toggle. Mobile: hidden. */}
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setCollapsed(!collapsed)}
              visibleFrom="sm"
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <IconLayoutSidebarLeftExpand size={18} />
              ) : (
                <IconLayoutSidebarLeftCollapse size={18} />
              )}
            </ActionIcon>
            <Text visibleFrom="sm" fw={600} fz="sm" c="dimmed">
              {activeLabel()}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <ThemeToggle />
            <UserMenu session={auth.session!} onLogout={() => void onLogout()} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {/* Force a fresh key when the drawer opens/closes so internal
            scroll positions reset cleanly. */}
        <ScrollArea.Autosize mah="100%" key={`nav-${collapsed}-${mobileOpen}`}>
          <Stack gap={0} h="100%">
            {/* Brand lockup — wordmark hidden when collapsed */}
            <Box px="xs" py="sm">
              {collapsed ? (
                <Box visibleFrom="sm" style={{ display: "flex", justifyContent: "center" }}>
                  <Logo showWordmark={false} size={18} />
                </Box>
              ) : (
                <Logo size={56} />
              )}
            </Box>

            {/* Nav sections */}
            <Box mt="xs" style={{ flex: 1 }}>
              <Stack gap={4}>
                {NAV_SECTIONS.flatMap((section, sectionIdx) => {
                  const children: ReactNode[] = [];
                  if (section.label && (!collapsed || mobileOpen)) {
                    children.push(
                      <Text
                        key={`section-${sectionIdx}-label`}
                        size="xs"
                        fw={600}
                        c="dimmed"
                        tt="uppercase"
                        pl="sm"
                        pt="sm"
                        pb={4}
                        style={{ letterSpacing: "0.06em" }}
                      >
                        {section.label}
                      </Text>
                    );
                  } else if (section.label && collapsed && !mobileOpen) {
                    // Keep a visual gap when collapsed so sections stay
                    // separated in the icon rail.
                    children.push(<Box key={`section-${sectionIdx}-gap`} h={8} />);
                  }
                  section.items.forEach((item) => children.push(renderNavItem(item)));
                  return children;
                })}
              </Stack>
            </Box>
          </Stack>
        </ScrollArea.Autosize>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
